import * as yup from "yup";
import { Flatten } from "../../types/FlattenType";
import { QuoteType } from "../../types/QuoteType";
import { YesNo } from "../enums";
import { RentalQuote, Scenario } from "./rental.model";

export class RentalForm implements Flatten<RentalQuote> {
  quoteName: string = "New Rental Quote";
  brokerFee: number = 0;
  borrowerIsReferral: YesNo = YesNo.No;
  appraisedVal1: number = 0;
  creditScore: number = 0;
  submissionType: string = "";
  annualTaxes: number = 0;
  propertiesNY?: YesNo = YesNo.No;
  annualInsurance: number = 0;
  refiL1cFixNFlip: YesNo = YesNo.No;
  buyUpDownOption: string = "No Buy Up/Buy Down";
  username: string = "";
  numOfProperties: number = 1;
  overrideMaster: boolean = false;
  annualFloodInsurance: number = 0;
  borrowingEntity: string = "";
  mortgagePayoffs: number = 0;
  mortgagePayoffs2: number = 0;
  quoteType: QuoteType = "Rental";
  appraisedVal3: number = 0;
  totalCosts1: number = 0;
  renovationCostsAfterPurchase?: number = 0;
  monthlyRent: number = 0;
  sreoPropertyNumber: number = 0;
  productType: string = "";
  purchasePrice: number = 0;
  prepaymentPenalty: string = "";
  borrowerNetWorth: number = 0;
  yieldSpread: number = 0;
  originationFeeAdjustment?: number = 1.5;
  annualHOA: number = 0;
  buyUpDownPercentage: number = 0;
  brokerProcessingFee: number = 0;
  appraisalFee: number = 0;
  condo: YesNo = YesNo.No;
  twoFourUnit: YesNo = YesNo.No;
  vacantProperty: YesNo = YesNo.No;
  fixToRent: YesNo = YesNo.No;
  propertyVal: YesNo = YesNo.No;
  usCitizen: YesNo = YesNo.Yes;
  isTier5Borrower: YesNo = YesNo.No;
  finalOrigFeeOverride?: number | string = "";
  finalRateOverride?: number | string = "";
  rateReductionOverride?: number = 0;
  exceptionType?: string = "";
  autoRate?: number | string = 0;
  autoFee?: number | string = 0;
  commissionReduction?: number | string = 0;
  isZeroOrigFee?: YesNo = YesNo.No;
  underwriterStatus?: string = "";
  seasonalRental?: YesNo = YesNo.No;
  fiveNineUnit?: YesNo = YesNo.No;
  state?: string = "";
  legalFee?: number | string = "";
  taxImpound?: number | string = "";
  insuranceImpound?: number | string = "";
  propertyAddress?: string = "";
  city?: string = "";
  zip?: string = "";
  propertyType?: string = "";
  formattedAddress?: string = "";
  interestRateAddOn?: number | string = 0;
  originationPointsAddOn?: number | string = 0;
  applicationFee?: number | string = "";
  documentFee?: number | string = "";
  processingFee?: number | string = 0;
  lenderFinanceAppraisalFee?: number | string = "";
  rpolt6?: boolean;
  rpogt6?: boolean;
  purchase?: boolean;
  submittedDate?: string = "";
  iREscrowOverrideInt?: number;
  iREscrowOverrideMoneyAmt?: number;
  processingFeeOverride?: number;
  taxEscrowAmount?: number;
  insEscrowAmount?: number;
  achExempt?: string = "Non-exempt";
  achRequired?: string = YesNo.Yes;
  splitProcessingFee?: string;
  originalPurchasePrice?: number = 0;
  originalPurchaseDate?: string;
  targetCloseDate?: string = "";
  wholesalePartner?: string = "";
  documentPrepFee?: string = "";

  propertyManagementFee: number = 0;
  collateralCondos: YesNo = YesNo.No;
  collateral: YesNo = YesNo.No;
  index: string = "5yr I/R Swap";
  openPrepayOption: string = "Open Prepayment Allowance: 0% UPB/Year";

  sfaccountid?: string = "";
  sfdealid?: string = "";
  campaignId?: string = "";
  campaignName?: string = "";
  campaignExpirationDate?: string = "";
  campaignIncentive?: string = "";
  campaignCode?: string = "";

  constructor(entity: any) {
    if (!entity) return;

    this.quoteName = entity.quoteName ?? "New Rental Quote";
    this.brokerFee = entity.brokerFee;
    this.borrowerIsReferral = entity.borrowerIsReferral;
    this.appraisedVal1 = entity.appraisedVal1;
    this.creditScore = entity.creditScore;
    this.submissionType = entity.submissionType ?? "Standard Retail";
    this.annualTaxes = entity.annualTaxes;
    this.propertiesNY = entity?.propertiesNY;
    this.annualInsurance = entity.annualInsurance;
    this.refiL1cFixNFlip = entity.refiL1cFixNFlip;
    this.buyUpDownOption = entity.buyUpDownOption;
    this.username = entity.username;
    this.numOfProperties = entity.numOfProperties;
    this.overrideMaster = entity.overrideMaster;
    this.annualFloodInsurance = entity.annualFloodInsurance;
    this.borrowingEntity = entity.borrowingEntity;
    this.mortgagePayoffs = entity.mortagePayoffs ?? entity.mortgagePayoffs;
    this.quoteType = entity.quoteType;
    this.appraisedVal3 = entity.appraisedVal3;
    this.totalCosts1 = entity.totalCosts1;
    this.renovationCostsAfterPurchase = entity.renovationCostsAfterPurchase;
    this.monthlyRent = entity.monthlyRent;
    this.sreoPropertyNumber = entity.sreoPropertyNumber;
    this.productType = entity.productType;
    this.purchasePrice = entity.purchasePrice;
    this.prepaymentPenalty = entity.prepaymentPenalty;
    this.borrowerNetWorth = entity.borrowerNetWorth;
    this.yieldSpread = entity.yieldSpread ?? entity.yieldSpendPremium;
    this.originationFeeAdjustment =
      entity.originationFeeAdjustment ?? 1.5;
    this.annualHOA = entity.annualHOA;
    this.buyUpDownPercentage = entity.buyUpDownPercentage;
    this.brokerProcessingFee = entity.brokerProcessingFee;
    this.appraisalFee = entity.appraisalFee;
    this.condo = entity.base.condo;
    this.vacantProperty = entity.vacantProperty;
    this.twoFourUnit = entity.twoFourUnit;
    this.fixToRent = entity.base.fixToRent;
    this.propertyVal = entity.base.propertyVal;
    this.usCitizen = entity.usCitizen ?? YesNo.Yes;
    this.isTier5Borrower = entity.isTier5Borrower ?? YesNo.No;
    this.finalRateOverride =
      entity.exceptionPricingOverride.finalRateOverride ?? "";
    this.finalOrigFeeOverride =
      entity?.exceptionPricingOverride.finalOrigFeeOverride ??
      this.finalOrigFeeOverride;
    this.rateReductionOverride =
      entity?.exceptionPricingOverride.rateReductionOverride ??
      this.rateReductionOverride;
    this.autoFee = entity?.exceptionPricingOverride.autoFee ?? 0;
    this.autoRate = entity?.exceptionPricingOverride.autoRate ?? 0;
    this.commissionReduction =
      entity.exceptionPricingOverride.commissionReduction ?? 0;
    this.achRequired = entity.exceptionPricingOverride.achRequired ?? YesNo.Yes;
    this.achExempt = entity.exceptionPricingOverride.achExempt ?? "Non-exempt";
    this.splitProcessingFee =
      entity.exceptionPricingOverride.splitProcessingFee ?? 0;
    this.mortgagePayoffs2 = entity.mortgagePayoffs2 ?? 0;
    this.targetCloseDate = entity.targetCloseDate ?? "";
    this.originalPurchaseDate = entity.originalPurchaseDate ?? "";
    this.originalPurchasePrice = entity.originalPurchasePrice ?? 0;
    this.wholesalePartner = entity.wholesalePartner ?? "";
    this.documentPrepFee = entity.documentPrepFee ?? "";

    this.isZeroOrigFee = entity?.isZeroOrigFee ?? YesNo.No;
    this.exceptionType =
      entity?.exceptionPricingOverride.exceptionType ?? this.exceptionType;
    this.seasonalRental = entity?.seasonalRental ?? YesNo.No;
    this.fiveNineUnit = entity?.fiveNineUnit ?? YesNo.No;
    this.state = entity?.state ?? entity?.state;
    this.legalFee = entity?.underwriterFields?.legalFee ?? this.legalFee;
    this.taxImpound = entity?.underwriterFields?.taxImpound ?? this.taxImpound;
    this.insuranceImpound =
      entity?.underwriterFields?.insuranceImpound ?? this.insuranceImpound;
    this.submittedDate = entity?.submittedDate ?? "";

    this.interestRateAddOn =
      entity?.lenderFinance?.interestRateAddOn !== ""
        ? entity?.lenderFinance?.interestRateAddOn
        : 0;
    this.originationPointsAddOn =
      entity?.lenderFinance?.originationPointsAddOn !== ""
        ? entity?.lenderFinance?.originationPointsAddOn
        : 0;
    this.applicationFee =
      entity?.lenderFinance?.applicationFee ?? this.applicationFee;
    this.documentFee = entity?.lenderFinance?.documentFee ?? this.documentFee;
    this.lenderFinanceAppraisalFee =
      entity?.lenderFinance?.lenderFinanceAppraisalFee ??
      this.lenderFinanceAppraisalFee;
    this.processingFee =
      entity?.lenderFinance?.processingFee !== ""
        ? entity?.lenderFinance?.processingFee
        : 0;
    this.rpolt6 = entity?.lenderFinance?.rpolt6 ?? this.rpolt6;
    this.rpogt6 = entity?.lenderFinance?.rpogt6 ?? this.rpogt6;
    this.purchase = entity?.lenderFinance?.purchase ?? this.purchase;

    this.iREscrowOverrideInt = entity.loanCostOverride?.iREscrowOverrideInt;
    this.iREscrowOverrideMoneyAmt =
      entity.loanCostOverride?.iREscrowOverrideMoneyAmt;
    this.processingFeeOverride =
      entity.exceptionPricingOverride?.processingFeeOverride;
    this.taxEscrowAmount = entity.loanCostOverride?.taxEscrowAmount;
    this.insEscrowAmount = entity.loanCostOverride?.insEscrowAmount;

    this.propertyManagementFee =
      entity?.propertyManagementFee ?? this.propertyManagementFee;
    this.collateralCondos = entity?.collateralCondos ?? this.collateralCondos;
    this.collateral = entity?.collateral ?? this.collateral;
    this.index = entity?.index ?? this.index;
    this.openPrepayOption = entity?.openPrepayOption ?? this.openPrepayOption;

    this.sfaccountid = entity?.sfaccountid ?? "";
    this.sfdealid = entity?.sfdealid ?? "";
    this.campaignId = entity?.campaignId ?? "";
    this.campaignCode = entity?.campaignCode ?? "";
    this.campaignExpirationDate = entity?.campaignExpirationDate ?? "";
    this.campaignIncentive = entity?.campaignIncentive ?? "";
    this.campaignName = entity?.campaignName ?? "";
    this.propertyAddress = entity?.propertyAddress ?? "";
    this.city = entity?.city ?? "";
    this.zip = entity?.zip ?? "";
  }
}

export class RentalPremierForm extends RentalForm {
  constructor(entity: any) {
    super(entity);

    this.quoteType = "Premier";
    this.quoteName = entity?.quoteName ?? "New Rental Premier Quote";
    this.propertyManagementFee =
      entity?.propertyManagementFee ?? this.propertyManagementFee;
    this.collateralCondos = entity?.collateralCondos ?? this.collateralCondos;
    this.collateral = entity?.collateral ?? this.collateral;
    this.index = entity?.index ?? this.index;
    this.openPrepayOption = entity?.openPrepayOption ?? this.openPrepayOption;
  }
}

export class RentalScenarios implements Scenario {
  scenarioId: number = 1;
  ltvPercentage: number = 75;
  included: YesNo = YesNo.Yes;
  preferred: YesNo = YesNo.No;

  constructor(entity: any) {
    if (!entity) return;

    this.scenarioId = entity.scenarioId;
    this.ltvPercentage = entity.ltvPercentage;
    this.included = entity.included;
    this.preferred = entity.preferred;
  }
}

// For the side navigation
// Break the rental form schema up into it's
// sections.

// Final object will be a spread of each section

// As the form navigation active id === form section
// then we determine whether or not the values match the
// schema. thus giving the validation feedback.

const baseYupObject = {
  quoteName: yup.string().required("Quote Name is required"),
  brokerFee: yup
    .number()
    .min(0, "Negative values are not permitted for this selection")
    .required("Broker Fee is required"),
  borrowerIsReferral: yup.string().required(),
  appraisedVal1: yup.number().required("Appraised Value is required"),
  creditScore: yup
    .number()
    .required("Credit Score is required")
    .min(0, "Credit Score must be 0 or greater than or equal to 300")
    .max(850, "Credit Score must be less than or equal to 850"),
  submissionType: yup.string().required("Submission Type is required"),
  annualTaxes: yup.number().required("Annual Taxes is required"),
  propertiesNY: yup.string(),
  annualInsurance: yup.number().required("Annual Insurance is required"),
  refiL1cFixNFlip: yup.string().required(),
  username: yup.string(),
  numOfProperties: yup.number().required("Number of Properties is required"),
  overrideMaster: yup.boolean().required(),
  annualFloodInsurance: yup
    .number()
    .required("Annual Flood Insurance is required"),
  borrowingEntity: yup.string().required("Borrowing Entity is required"),
  mortgagePayoffs: yup.number().required("Mortgage Payoff is required"),
  originalPurchasePrice: yup
    .number()
    .required("Original Purchase Price is required"),
  originalPurchaseDate: yup.string(),
  targetCloseDate: yup.string(),
  mortgagePayoffs2: yup.number().required("Mortgage Payoffs is required"),
  quoteType: yup.string().required("Quote Type is required"),
  appraisedVal3: yup.number().required("Appraised Value is required"),
  totalCosts1: yup.number().required("Total Costs is required"),
  renovationCostsAfterPurchase: yup.number(),
  monthlyRent: yup
    .number()
    .min(1, "Value must be greater than 0")
    .required("Monthly Rent is required"),
  sreoPropertyNumber: yup.number().required("SREO Property Number is required"),
  productType: yup.string().required("Product Type is required"),
  purchasePrice: yup.number().required("Purchase Price is required"),
  prepaymentPenalty: yup.string().required("Prepayment Penalty is required"),
  borrowerNetWorth: yup.number().required("Borrower Net Worth is required"),
  yieldSpread: yup.number().required("Yield Spend Premium is required"),
  annualHOA: yup.number().required("Annual HOA is required"),
  buyUpDownPercentage: yup
    .number(),
  brokerProcessingFee: yup
    .number()
    .required("Broker Processing Fee is required"),
  appraisalFee: yup.number().required("Valuation Fee is required"),
  condo: yup.string().required(),
  fixToRent: yup.string().required(),
  propertyVal: yup.string().required(),
  usCitizen: yup.string().required(),
  isTier5Borrower: yup.string().required(),
  finalOrigFeeOverride: yup.number(),
  commissionReduction: yup.number(),
  rateReductionOverride: yup.number(),
  finalRateOverride: yup.number(),
  isZeroOrigFee: yup.string(),
  exceptionType: yup.string(),
  seasonalRental: yup.string(),
  fiveNineUnit: yup.string(),
  autoRate: yup.number(),
  autoFee: yup.number(),
  achExempt: yup.string(),
  achRequired: yup.string(),
  splitProcessingFee: yup.string(),
  wholesalePartner: yup.string(),
  state: yup.string().required("Property State is Required"),
  propertyAddress: yup.string().required("Street is required"),
  city: yup.string().required("City is required"),
  zip: yup
    .string()
    .matches(/(^\d{5}$)|(^\d{5}-\d{4}$)/, "Invalid Zip Code")
    .required("Zip is required"),
};

const externalYupObject = {
  ...baseYupObject,
  brokerFee: yup
    .number()
    .min(0, "Negative values are not permitted for this selection")
    .max(3, "Fee percentage must be less than 3")
    .required("Broker Fee is required"),
};

export const RentalFormSchema = yup.object(baseYupObject);

export const ExternalRentalFormSchema = yup.object(externalYupObject);

export class RentalBorrowerInformation {
  quoteName: string = "";
  numOfProperties: number = 0;
  productType: string = "";
  submissionType: string = "";
  borrowingEntity: string = "";
  borrowerNetWorth: number = 0;
  creditScore: number = 0;
  sreoPropertyNumber: number = 0;
  usCitizen: YesNo = YesNo.No;
  isTier5Borrower: YesNo = YesNo.No;
  wholesalePartner: string = "";
}

export class RentalPropertySummary {
  appraisedVal1: number = 0;
  totalCosts1: number = 0;
  appraisedVal3: number = 0;
  purchasePrice: number = 0;
  mortgagePayoffs: number = 0;
  monthlyRent: number = 0;
  annualInsurance: number = 0;
  annualFloodInsurance: number = 0;
  annualTaxes: number = 0;
  annualHOA: number = 0;
  propertyManagementFee: number = 0;
  state?: string = "";
  propertyAddress?: string = "";
  city?: string = "";
  zip?: string = "";
  propertyType?: string = "";
  formattedAddress?: string = "";
  mortgagePayoffs2: number = 0;
  originalPurchasePrice?: number = 0;
  originalPurchaseDate?: string = "";
  targetCloseDate?: string = "";
}

export class RentalPricing {
  yieldSpread: number = 0;
  originationFeeAdjustment?: number = 1.5;
  brokerFee: number = 0;
  brokerProcessingFee: number = 0;
  buyUpDownOption: number = 0;
  buyUpDownPercentage: number = 0;
  prepaymentPenalty: number = 0;
  appraisalFee: number = 0;
  refiL1cFixNFlip: YesNo = YesNo.No;
  propertiesNY?: YesNo = YesNo.No;
  propertyVal: YesNo = YesNo.No;
  referral: YesNo = YesNo.No;
  condo: YesNo = YesNo.No;
  isZeroOrigFee?: YesNo = YesNo.No;
  seasonalRental?: YesNo = YesNo.No;
  fiveNineUnit?: YesNo = YesNo.No;
}

export class RentalPremierPricing extends RentalPricing {
  openPrepayOption: string = "";
  collateral: YesNo = YesNo.No;
  referral: YesNo = YesNo.No;
  collateralCondos: YesNo = YesNo.No;
}
